'use client';
import BadgeWithTooltip, {
    EBadgeWithTooltip,
} from '@/components/shared/badege-with-tooltip/badge-with-tooltip.component';
import Carousel from '@/components/shared/carousel/carousel.component';
import ConditionalClientComponent from '@/components/shared/conditional/conditional-client.component';
import Typography from '@/components/shared/typography/typography.component';
import {
    CalendarCardIcon,
    Speedometer,
    TransmissionIcon,
    WhiteArrowDownGreenBackground,
} from '../../../../public/Images/Icons';

import Link from '@/components/WrappedLink';

import { useTranslation } from '@/hook';

import { InfoOutlinedIcon } from './reserved-tooltip.component';
import { useAnalytics } from '@/integrations';
import styles from './styles.module.scss';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { NewlyAddedAr, NewlyAddedEn } from '../../../../public/Images';
import colors from '@/theme/colors.module.scss';
import { isValidUUid } from '@/utils/uuidV4.util';

import { EReservationTagsVariants } from '@/types';
import { FEATURE_FLAG_RESERVATION_TAGS } from '@/constants';
import { useFeatureValue } from '@growthbook/growthbook-react';

import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { carPricePercentage } from '@/constants/carPriceDiscounts';

const StyledToolTip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
        arrow
        sx={{
            display: 'block',
            marginInlineEnd: 'auto',
            backgroundColor: 'white',
        }}
    />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.white,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: '#344054',
        border: '1px solid #EAECF0',
    },
}));
export const TemporaryReserveBadge = () => {
    const { t } = useTranslation();
    return (
        <StyledToolTip
            title={
                <Typography variant="captionMedium">
                    {t('reservedBadgeTooltipDescription')}
                </Typography>
            }
        >
            <div>
                <Typography
                    component="div"
                    variant="body2Medium"
                    className={styles.badge}
                >
                    {t('reservedFor48h')}
                </Typography>
            </div>
        </StyledToolTip>
    );
};

const CarDisplayCardClient = ({
    id,
    cardTitle,
    carName,
    installmentPrice,
    totalPrice,
    kilometers,
    year,
    transmission,
    images,
    trim,
    isReserved,
    isPurchaseInProgress,
    make,
    model,
    sourceScreen,
    appliedFilter,
    appliedSort,
    position,
    discountPrice,
    isNewlyAdded,
}: {
    id: string;
    cardTitle: string;
    carName: string;
    installmentPrice: number | string;
    totalPrice: number | string;
    kilometers: number | string;
    year: number;
    transmission: string;
    images: { image: string }[];
    trim: { ar: string; en: string };
    isReserved: boolean;
    isPurchaseInProgress: boolean;
    make: string;
    model: string;
    sourceScreen: string;
    appliedFilter: boolean;
    appliedSort: boolean;
    position: number;
    discountPrice: number;
    isNewlyAdded: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const router = useRouter();

    const IS_RTL = lang === 'ar';

    const { trackEvent } = useAnalytics();

    const reservationTagVariant = useFeatureValue(
        FEATURE_FLAG_RESERVATION_TAGS,
        EReservationTagsVariants.ORIGINAL
    );

    const mainPrice = {
        color: '#009B5D',
        lineHeight: '32px',
        opacity: 1,
    };
    const mainPriceWithDsicount = {
        // textDecoration: 'line-through',
        fontSize: '0.75rem',
    };

    const numberWithCommas = (number: number | string = 0) => {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    function goToCarPage() {
        trackEvent('Action: Click on Car Card', {
            carName,
            make,
            model,
            year,
            trim,
            kilometers,
            price: totalPrice,
            monthlyInstallment: installmentPrice,
            status: isPurchaseInProgress
                ? 'Being Sold'
                : isReserved
                ? 'Reserved'
                : 'Not Reserved',
            transmission,
            sourceScreen,
            appliedFilter,
            appliedSort,
            position,
            newlyAdded: isNewlyAdded && !(isReserved || isPurchaseInProgress),
        });
        if (isValidUUid(id)) {
            router.push(`/en/car-details/${id}`);
        }

        // window.location.reload();
    }

    const formatNumber = (number: number) => {
        const abbreviations = {
            K: 1000,
            M: 1000000,
        };

        const keys = Object.keys(abbreviations).reverse();

        for (const key of keys) {
            if (number >= abbreviations[key as keyof typeof abbreviations]) {
                const resultNumber = (
                    number / abbreviations[key as keyof typeof abbreviations]
                ).toFixed(0);
                const arText = `${t('Off')} ${resultNumber} ${t('K')} ${t(
                    'limitedOffer'
                )}`;
                const enText = `${resultNumber}${t('K')} ${t('Off')} ${t(
                    'limitedOffer'
                )}`;
                return lang === 'ar' ? arText : enText;
            }
        }

        return number.toString();
    };
    const newOfferText = (number: number) => {
        const arText = `${number}% أقل من أسعار فبراير`;
        const enText = `${number}% decrease from Feb Market Prices`;
        return lang === 'ar' ? arText : enText;
    };
    const hasDiscount = discountPrice !== undefined && discountPrice !== 0;
    const differencs = Math.trunc(
        parseFloat(totalPrice as string) - discountPrice
    );

    const discountPercentage = formatNumber(differencs);

    const getToolTipType = () => {
        if (isPurchaseInProgress) {
            return EBadgeWithTooltip.PURCHASE_IN_PROGRESS;
        } else if (isReserved) {
            return EBadgeWithTooltip.RESERVED;
        } else {
            return EBadgeWithTooltip.null;
        }
    };

    const CardBadge = () => {
        return (
            <>
                {!isPurchaseInProgress &&
                isReserved &&
                reservationTagVariant &&
                reservationTagVariant != EReservationTagsVariants.ORIGINAL ? (
                    <>
                        {reservationTagVariant ==
                            EReservationTagsVariants.CAR_LISTING_AND_PROFILE && (
                            <div className={styles.badgeContainer}>
                                <TemporaryReserveBadge />
                            </div>
                        )}
                    </>
                ) : isReserved || isPurchaseInProgress ? (
                    <div className={styles.badgeContainer}>
                        <BadgeWithTooltip type={getToolTipType()} />
                    </div>
                ) : (
                    isNewlyAdded && (
                        <div className={styles.newlyAddedBadge}>
                            <Image
                                src={IS_RTL ? NewlyAddedAr : NewlyAddedEn}
                                alt={'newly-added'}
                                width={70}
                                height={47}
                            />
                        </div>
                    )
                )}
            </>
        );
    };
    const newTagIsActive = () => {
        return (
            reservationTagVariant &&
            reservationTagVariant ==
                EReservationTagsVariants.CAR_LISTING_AND_PROFILE
        );
    };
    const getInfoDisplayFlag = () => {
        let flag = false;
        if (isPurchaseInProgress) return isPurchaseInProgress;
        if (isReserved) {
            flag =
                reservationTagVariant == EReservationTagsVariants.ORIGINAL ||
                newTagIsActive();
        }
        return flag;
    };

    return (
        isValidUUid(id) && (
            <Link
                prefetch={false}
                className={styles.card_container}
                href={`/${lang}/car-details/${id}`}
                onClick={() => goToCarPage()}
            >
                <CardBadge />
                <div className={styles.image_container}>
                    {images && (
                        <Carousel
                            slideShow={true}
                            dragable={false}
                            showDots={true}
                            imageMargin="0"
                            width={320}
                            height={240}
                            quality={45}
                            containerWidth="100%"
                            imgs={images.slice(0, 4)}
                            lang={lang as 'ar' | 'en'}
                        ></Carousel>
                    )}
                    <ConditionalClientComponent
                        condition={carPricePercentage[id]}
                    >
                        <div
                            style={{
                                right: IS_RTL ? 'unset' : '0px',
                                left: IS_RTL ? '0px' : 'unset',
                            }}
                            className={styles.new_offer_container}
                        >
                            <Image
                                src={WhiteArrowDownGreenBackground}
                                alt={'white-arrow-green-background'}
                                width={18}
                                height={18}
                                className={styles.new_offer_image}
                                style={{
                                    transform: IS_RTL ? 'scaleX(-1)' : '',
                                }}
                            />
                            <Typography
                                component="div"
                                className={styles.new_offer_text}
                                variant={'captionMedium'}
                            >
                                {`${newOfferText(carPricePercentage[id])}`}
                            </Typography>
                        </div>
                    </ConditionalClientComponent>
                </div>
                <div className={styles.content_container}>
                    <Typography
                        className={styles.card_header_text}
                        variant={'body1Medium'}
                    >
                        {cardTitle}
                    </Typography>
                    <Typography
                        className={styles.card_sub_header_text}
                        variant={'body2'}
                    >
                        {IS_RTL ? trim?.ar : trim?.en}
                    </Typography>
                    {/* price details part */}
                    <div className={styles.card_price_container}>
                        <ConditionalClientComponent condition={hasDiscount}>
                            <>
                                <div style={{ display: 'flex' }}>
                                    <Typography
                                        variant={'caption'}
                                        className={
                                            lang === 'ar'
                                                ? 'strikethroughAr'
                                                : 'strikethrough'
                                        }
                                        style={{
                                            fontWeight: '600',
                                            marginInline: '2px',
                                            fontSize: '1rem',
                                            color: '#B54708',
                                        }}
                                    >
                                        {`${numberWithCommas(totalPrice)}`}
                                    </Typography>
                                    <Typography
                                        style={{
                                            marginInline: '2px',
                                            color: '#B54708',
                                        }}
                                        variant={'caption'}
                                    >
                                        {t('EGP')}
                                    </Typography>
                                </div>
                                <div
                                    style={{
                                        backgroundColor: '#FFFAEB',
                                        padding: ' 4px 10px 4px 10px',
                                        borderRadius: '24px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        width: 'max-content',
                                        margin: '4px',
                                    }}
                                >
                                    <Typography
                                        style={
                                            hasDiscount
                                                ? {
                                                      ...mainPrice,
                                                      ...mainPriceWithDsicount,
                                                      lineHeight: '1.25rem',
                                                      color: '#B54708',
                                                  }
                                                : mainPrice
                                        }
                                        variant={'subtitle1SemiBold'}
                                    >
                                        {`${discountPercentage}`}
                                    </Typography>
                                </div>
                            </>
                        </ConditionalClientComponent>
                    </div>
                    <div
                        className={styles.card_price_container}
                        style={{
                            justifyContent: 'space-between',
                            marginTop: 'auto',
                        }}
                    >
                        <div className={styles.card_content_text}>
                            <ConditionalClientComponent condition={hasDiscount}>
                                <>
                                    <Typography
                                        className={styles.discount_price}
                                        variant={'subtitle1SemiBold'}
                                    >
                                        {numberWithCommas(discountPrice)}
                                    </Typography>
                                    <Typography
                                        color="#667085"
                                        style={{ marginInline: '4px' }}
                                        variant={'caption'}
                                    >
                                        {t('EGP')}
                                    </Typography>
                                </>
                            </ConditionalClientComponent>
                            <ConditionalClientComponent
                                condition={!hasDiscount}
                            >
                                <>
                                    <Typography
                                        color="#009B5D"
                                        variant={'subtitle1SemiBold'}
                                    >
                                        {numberWithCommas(totalPrice)}
                                    </Typography>
                                    <Typography
                                        color="#667085"
                                        style={{ marginInline: '4px' }}
                                        variant={'caption'}
                                    >
                                        {t('EGP')}
                                    </Typography>
                                </>
                            </ConditionalClientComponent>
                        </div>
                        {installmentPrice && (
                            <Typography
                                color="#002D5D"
                                variant={'body2SemiBold'}
                            >
                                {/* {t('from')}  */}
                                {numberWithCommas(installmentPrice)}
                                {t('/month')}
                            </Typography>
                        )}
                    </div>
                    {/* more details part */}
                    <div className={styles.more_details_container}>
                        <div className={styles.more_details_items}>
                            <Image
                                src={Speedometer}
                                width={20}
                                height={20}
                                className={styles.more_details_item_icon}
                                alt="kilometers"
                            />
                            <Typography color="#667085" variant={'body2'}>
                                {numberWithCommas(kilometers)} {t('km')}
                            </Typography>
                        </div>
                        <div className={styles.more_details_items}>
                            <Image
                                width={20}
                                height={20}
                                src={CalendarCardIcon}
                                alt="year"
                                className={styles.more_details_item_icon}
                            />
                            <Typography color="#667085" variant={'body2'}>
                                {year}
                            </Typography>
                        </div>
                        <div className={styles.more_details_items}>
                            <Image
                                width={20}
                                height={20}
                                src={TransmissionIcon}
                                className={styles.more_details_item_icon}
                                alt="transmission"
                            />
                            <Typography color="#667085" variant={'body2'}>
                                {transmission === 'automatic'
                                    ? t('automatic')
                                    : t('manual')}
                            </Typography>
                        </div>
                    </div>
                    {/* reserve info on mobile */}
                    {getInfoDisplayFlag() && (
                        <div
                            className={`${styles.reserved_disclaimer} ${
                                isPurchaseInProgress &&
                                styles.being_sold_disclaimer
                            }
                            ${
                                isReserved &&
                                newTagIsActive() &&
                                styles.new_reserve
                            }
                            `}
                        >
                            <InfoOutlinedIcon
                                fontSize={'small'}
                                color={
                                    isPurchaseInProgress
                                        ? 'secondary'
                                        : newTagIsActive()
                                        ? 'secondary'
                                        : 'success'
                                }
                            />
                            <Typography
                                color={
                                    isPurchaseInProgress
                                        ? colors.secondary
                                        : newTagIsActive()
                                        ? colors.gray600
                                        : colors.success600
                                }
                                style={{ textAlign: 'start' }}
                                variant="captionMedium"
                            >
                                {isPurchaseInProgress
                                    ? t('purchaseInProgress.descriptionLong')
                                    : t('reservedBadgeTooltipDescription')}
                            </Typography>
                        </div>
                    )}
                </div>
            </Link>
        )
    );
};

export default CarDisplayCardClient;
